import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { shortcutsRegistry } from './registry';
import type { Shortcut, ShortcutGroup } from './types';

/**
 * Programmatic open hook for the shortcuts help sheet. Dispatches the
 * same `?` keydown the sheet already self-listens for, so callers
 * (e.g. the `/help` index page CTA) don't have to know about the
 * subscriber wiring. Skips the input-field guard because the dispatch
 * target is `window`, not a form control.
 */
export function openHelpSheet(): void {
  window.dispatchEvent(new KeyboardEvent('keydown', { key: '?' }));
}

/**
 * Help sheet listing every currently-active shortcut, grouped by `group`.
 *
 * Mounted in the app shell next to {@link ShortcutsProvider}. Self-toggles
 * via a global `?` keydown listener — registered here rather than as a
 * shortcut so the help sheet stays available even if the registry is
 * temporarily empty (e.g. during HMR or test reset).
 */
export function HelpSheet(): JSX.Element {
  const [open, setOpen] = useState(false);
  const { t } = useTranslation();

  const all = useSyncExternalStore(
    shortcutsRegistry.subscribe,
    shortcutsRegistry.list,
    shortcutsRegistry.list,
  );

  // The `listActive` snapshot isn't auto-subscribed; recompute on render.
  const active = useMemo(
    () => all.filter((s) => (s.scope ?? 'global') === 'global' || isActiveScope(s)),
    [all],
  );

  const grouped = useMemo<Record<ShortcutGroup, Shortcut[]>>(() => {
    const result: Record<ShortcutGroup, Shortcut[]> = {
      navigation: [],
      actions: [],
      help: [],
    };
    active.forEach((s) => {
      result[s.group].push(s);
    });
    return result;
  }, [active]);

  useEffect(() => {
    function handler(event: KeyboardEvent): void {
      if (event.key !== '?') return;
      const target = event.target as HTMLElement | null;
      if (target) {
        const tag = target.tagName;
        if (
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target.isContentEditable
        ) {
          return;
        }
      }
      event.preventDefault();
      setOpen((prev) => !prev);
    }
    window.addEventListener('keydown', handler);
    return () => {
      window.removeEventListener('keydown', handler);
    };
  }, []);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="w-[min(440px,90vw)]">
        <SheetTitle>{t('shortcuts.helpTitle')}</SheetTitle>
        <SheetDescription>{t('shortcuts.helpDescription')}</SheetDescription>

        <div className="mt-6 flex flex-col gap-6">
          <ShortcutGroupBlock
            heading={t('shortcuts.group.navigation')}
            items={grouped.navigation}
          />
          <ShortcutGroupBlock
            heading={t('shortcuts.group.actions')}
            items={grouped.actions}
          />
          <ShortcutGroupBlock
            heading={t('shortcuts.group.help')}
            items={grouped.help}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}

interface ShortcutGroupBlockProps {
  heading: string;
  items: Shortcut[];
}

function ShortcutGroupBlock({ heading, items }: ShortcutGroupBlockProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div>
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {heading}
      </h3>
      <ul className="flex flex-col gap-1.5">
        {items.map((s) => (
          <li
            key={s.id}
            className="flex items-center justify-between gap-3 rounded-sm px-2 py-1 text-sm"
          >
            <span className="text-foreground">{s.description}</span>
            <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-xs text-muted-foreground">
              {s.keys}
            </kbd>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * Helper used purely inside this component to mirror what the provider's
 * `listActive` filter would return; we can't call `listActive` directly
 * from `useSyncExternalStore` because it isn't memoized.
 */
function isActiveScope(_s: Shortcut): boolean {
  // The HelpSheet shows global + currently-active page-scoped shortcuts.
  // Page scopes are enumerated by `shortcutsRegistry.listActive()` but we
  // already start from the full list and filter by `(scope ?? 'global') ===
  // 'global'` above; non-global scopes are dropped here unless they're in
  // the active set. We re-derive that quickly:
  const active = shortcutsRegistry.listActive();
  return active.some((a) => a.id === _s.id);
}
